/**
 * Sponsor portal (`/sponsor-reports`, D11, §7.2): read-only, sponsor-scoped — placement impressions/clicks,
 * share-card reach, sample allocations and claims, thresholded variant preferences, branded CSV/PDF download.
 * Organizer staff with `sponsor.report.view` pick a sponsor; sponsor viewers are pinned to their own.
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import type { SponsorPortalMeDto, SponsorReportDto, SponsorReportExportDto } from '@windrop/contracts';
import { Button, Field, Select, useIntl } from '@windrop/ui';
import { useState } from 'react';
import { useSession } from '../../auth/session';
import { BarsChart } from '../../components/Chart';
import { ErrorState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { StatCard } from '../../components/StatCard';
import { api, buildUrl, isApiError, type QueryValue } from '../../lib/api/client';
import { catalogApi } from '../catalog/api';

const sponsorApi = {
  me: (sponsorId?: string) => api.get<SponsorPortalMeDto>('/v1/sponsor/me', { sponsorId }),
  report: (q: Record<string, QueryValue>) =>
    api.get<SponsorReportDto>('/v1/sponsor/reports', { ...q, format: 'JSON' }),
  exports: (sponsorId?: string) =>
    api.get<SponsorReportExportDto[]>('/v1/sponsor/reports/exports', { sponsorId }),
  createExport: (body: { format: 'CSV' | 'PDF'; lotteryId?: string; sponsorId?: string }) =>
    api.post<SponsorReportExportDto>('/v1/sponsor/reports/exports', body),
};

export function SponsorReportsPage() {
  const { session } = useSession();
  const intl = useIntl();
  const pinned = session?.sponsorId ?? undefined;
  const [sponsorId, setSponsorId] = useState<string | undefined>(pinned);
  const [lotteryId, setLotteryId] = useState('');
  const sponsors = useQuery({
    queryKey: ['sponsors'],
    queryFn: () => catalogApi.sponsors.list(),
    enabled: !pinned,
  });
  const me = useQuery({
    queryKey: ['sponsor-me', sponsorId],
    queryFn: () => sponsorApi.me(sponsorId),
    enabled: Boolean(sponsorId),
  });
  const report = useQuery({
    queryKey: ['sponsor-report', sponsorId, lotteryId],
    queryFn: () => sponsorApi.report({ sponsorId, lotteryId: lotteryId || undefined }),
    enabled: Boolean(sponsorId),
  });
  const exports = useQuery({
    queryKey: ['sponsor-exports', sponsorId],
    queryFn: () => sponsorApi.exports(sponsorId),
    enabled: Boolean(sponsorId),
  });
  const create = useMutation({
    mutationFn: (format: 'CSV' | 'PDF') =>
      sponsorApi.createExport({ format, lotteryId: lotteryId || undefined, sponsorId }),
    onSuccess: () => void exports.refetch(),
  });
  const downloadUrl = (format: 'CSV' | 'PDF') =>
    buildUrl('/v1/sponsor/reports', { format, sponsorId, lotteryId: lotteryId || undefined });

  if (!pinned && !sponsorId) {
    return (
      <>
        <PageHeader
          title="Sponsor reporting"
          breadcrumb={[{ label: 'Sponsor reporting' }]}
          description="Aggregate, thresholded figures per sponsor — never winner identity or contacts (D11)."
        />
        <Field label="Sponsor">
          <Select
            value=""
            onChange={(e) => setSponsorId(e.target.value || undefined)}
            options={(sponsors.data ?? []).map((s) => ({ value: s.sponsorId, label: s.name }))}
            placeholder="Choose a sponsor"
            data-testid="sponsor-select"
          />
        </Field>
      </>
    );
  }
  if (report.isError)
    return (
      <ErrorState
        title="Report not available"
        body="This sponsor is not linked to your login."
        reference={isApiError(report.error) ? report.error.supportReference : undefined}
        action={<Button href="/">Home</Button>}
      />
    );
  const r = report.data;
  const lotteries = r?.lotteries ?? [];
  const totals = lotteries.reduce(
    (acc, l) => ({
      impressions: acc.impressions + l.placements.reduce((n, p) => n + p.viewableImpressions, 0),
      clicks: acc.clicks + l.placements.reduce((n, p) => n + p.clicks, 0),
      allocated: acc.allocated + l.samples.allocated,
      claimed: acc.claimed + l.samples.claimed,
      delivered: acc.delivered + l.samples.delivered,
    }),
    { impressions: 0, clicks: 0, allocated: 0, claimed: 0, delivered: 0 },
  );
  const brand = me.data?.brandColor ?? undefined;
  return (
    <div
      data-testid="sponsor-portal"
      style={brand ? ({ ['--wd-sponsor' as string]: brand } as React.CSSProperties) : undefined}
    >
      <PageHeader
        title={me.data?.sponsorName ?? 'Sponsor reporting'}
        breadcrumb={[{ label: 'Sponsor reporting' }]}
        description={
          r
            ? `Window ${intl.formatDateTime(r.window.from)} – ${intl.formatDateTime(r.window.to)} · generated ${intl.formatRelative(r.generatedAt)}`
            : undefined
        }
        actions={
          <>
            <Button variant="secondary" href={downloadUrl('CSV')} data-testid="download-csv">
              Download CSV
            </Button>
            <Button variant="secondary" href={downloadUrl('PDF')} data-testid="download-pdf">
              Download PDF
            </Button>
          </>
        }
        meta={
          me.data?.logoLightUrl ? (
            <img src={me.data.logoLightUrl} alt={`${me.data.sponsorName} logo`} height={32} />
          ) : undefined
        }
      />
      {brand ? (
        <div aria-hidden style={{ height: 6, borderRadius: 3, background: brand, marginBottom: 16 }} />
      ) : null}
      <Field label="Lottery">
        <Select
          value={lotteryId}
          onChange={(e) => setLotteryId(e.target.value)}
          options={[
            { value: '', label: 'All my lotteries' },
            ...(me.data?.lotteries ?? []).map((l) => ({
              value: l.lotteryId,
              label: `${l.title} · ${l.tier}`,
            })),
          ]}
          data-testid="lottery-filter"
        />
      </Field>
      <div className="wdc-grid" data-testid="sponsor-totals">
        <StatCard
          label="Viewable impressions"
          value={r ? totals.impressions : null}
          loading={report.isPending}
          hint="Counted only when the placement met the viewability rule"
        />
        <StatCard
          label="Clicks"
          value={r ? totals.clicks : null}
          loading={report.isPending}
          hint={
            r && totals.impressions
              ? `CTR ${((totals.clicks / totals.impressions) * 100).toFixed(2)}%`
              : undefined
          }
        />
        <StatCard label="Samples allocated" value={r ? totals.allocated : null} loading={report.isPending} />
        <StatCard
          label="Samples claimed"
          value={r ? totals.claimed : null}
          loading={report.isPending}
          hint={r ? `${totals.delivered} delivered` : undefined}
        />
      </div>
      {lotteries.map((l) => (
        <section
          key={l.lotteryId}
          className="wd-surface"
          style={{ padding: 16, marginTop: 16 }}
          data-testid={`sponsor-lottery-${l.lotteryId}`}
        >
          <h2>
            {l.title} <span className="wd-muted">({l.tier})</span>
          </h2>
          <table className="wdc-table">
            <caption>Placement performance</caption>
            <thead>
              <tr>
                <th>Placement</th>
                <th>Viewable impressions</th>
                <th>Clicks</th>
                <th>CTR</th>
              </tr>
            </thead>
            <tbody>
              {l.placements.map((p) => (
                <tr key={p.placement}>
                  <td>{p.placement.replace(/_/g, ' ').toLowerCase()}</td>
                  <td>{p.viewableImpressions}</td>
                  <td>{p.clicks}</td>
                  <td>{(p.ctr * 100).toFixed(2)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="wd-muted">
            Share cards: {l.shareCardReach.generated} generated · {l.shareCardReach.referredVisits} referred
            visits. Samples: {l.samples.allocated} allocated, {l.samples.claimed} claimed,{' '}
            {l.samples.delivered} delivered, {l.samples.reservedRemaining} reserved remaining.
          </p>
          <BarsChart
            title="Variant preferences"
            description="Groups smaller than five are not shown (re-identification guard)."
            data={l.variantPreferences.map((v) => ({ label: `${v.axis}: ${v.value}`, count: v.count }))}
            xKey="label"
            series={[{ key: 'count', label: 'Winners' }]}
            emptyHint="No group has reached the reporting threshold yet."
          />
        </section>
      ))}
      <h2>Exports</h2>
      <div style={{ display: 'flex', gap: 8 }}>
        <Button variant="secondary" loading={create.isPending} onClick={() => create.mutate('CSV')}>
          Generate CSV export
        </Button>
        <Button variant="secondary" loading={create.isPending} onClick={() => create.mutate('PDF')}>
          Generate PDF export
        </Button>
      </div>
      <ul data-testid="sponsor-exports">
        {(exports.data ?? []).map((e) => (
          <li key={e.exportId}>
            {e.format} · {intl.formatDateTime(e.generatedAt)} · expires {intl.formatRelative(e.expiresAt)} ·{' '}
            <a href={buildUrl(`/v1/sponsor/reports/exports/${e.exportId}/download`, { sponsorId })}>
              download
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}
